import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { X, Loader2, ChevronDown } from 'lucide-react';
import { useCreateGroup } from '@/hooks/useGroups';
import type { GroupVisibility } from '@cc-consulting-nv/ccsdk';

interface CreateGroupModalProps {
  onClose: () => void;
}

const VISIBILITY_OPTIONS: { value: GroupVisibility; label: string; description: string }[] = [
  { value: 'PUBLIC', label: 'Public', description: 'Anyone can view and join' },
  { value: 'MODERATED', label: 'Moderated', description: 'Anyone can view, joining requires approval' },
  { value: 'PRIVATE', label: 'Private', description: 'Only members can view content' },
];

export default function CreateGroupModal({ onClose }: CreateGroupModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<GroupVisibility>('PUBLIC');
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  const createGroupMutation = useCreateGroup();

  const currentVisibility = VISIBILITY_OPTIONS.find((v) => v.value === visibility) || VISIBILITY_OPTIONS[0];

  useEffect(() => {
    gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' });
    gsap.fromTo(sheetRef.current, { y: '100%' }, { y: '0%', duration: 0.35, ease: 'power3.out' });
  }, []);

  const animateClose = useCallback((cb: () => void) => {
    gsap.to(sheetRef.current, { y: '100%', duration: 0.25, ease: 'power3.in' });
    gsap.to(overlayRef.current, { opacity: 0, duration: 0.25, ease: 'power2.in', onComplete: cb });
  }, []);

  const handleClose = useCallback(() => animateClose(onClose), [animateClose, onClose]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      await createGroupMutation.mutateAsync({ name: name.trim(), description: description.trim(), visibility });
      animateClose(onClose);
    } catch {
      /* error handled by mutation state */
    }
  };

  const canSubmit = name.trim().length > 0 && !createGroupMutation.isPending;

  return createPortal(
    <div ref={overlayRef} className="fixed inset-0 z-[95] bg-black/70" onClick={handleClose}>
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-[#0F1117] rounded-t-[24px] flex flex-col"
        style={{ maxHeight: '92vh', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-[#2A2F3A] rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] flex-shrink-0">
          <button onClick={handleClose} aria-label="Cancel" className="w-9 h-9 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]">
            <X size={18} />
          </button>
          <h2 className="text-base font-semibold text-[#F5F7FF]" style={{ fontFamily: 'Space Grotesk' }}>Create Group</h2>
          <button
            onClick={handleCreate}
            disabled={!canSubmit}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${canSubmit ? 'bg-[#A38BFF] text-[#0B0D10] active:scale-95' : 'bg-[#A38BFF]/30 text-[#A38BFF]/50 cursor-not-allowed'}`}
          >
            {createGroupMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : 'Create'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-4 py-4 pb-16">
          <div className="space-y-4 pb-64">
            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">Group name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={50}
                placeholder="Enter group name"
                className="w-full h-11 bg-[#141821] rounded-xl px-4 text-sm text-[#F5F7FF] placeholder-[#A7B0C8]/40 outline-none focus:ring-1 focus:ring-[#A38BFF]/50 transition-all"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={160}
                rows={3}
                placeholder="What is this group about?"
                className="w-full bg-[#141821] rounded-xl px-4 py-3 text-sm text-[#F5F7FF] placeholder-[#A7B0C8]/40 outline-none focus:ring-1 focus:ring-[#A38BFF]/50 transition-all resize-none"
              />
              <p className="text-xs text-[#A7B0C8] text-right mt-1">{description.length}/160</p>
            </div>

            {/* Visibility */}
            <div>
              <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">Visibility</label>
              <div className="relative">
                <button
                  onClick={() => setShowVisibilityMenu((v) => !v)}
                  className="w-full h-11 bg-[#141821] rounded-xl px-4 text-sm text-[#F5F7FF] flex items-center justify-between"
                >
                  <span>{currentVisibility.label}</span>
                  <ChevronDown size={16} className={`text-[#A7B0C8] transition-transform ${showVisibilityMenu ? 'rotate-180' : ''}`} />
                </button>
                {showVisibilityMenu && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-[#1A1F2E] rounded-xl border border-white/[0.08] shadow-xl z-10 overflow-hidden">
                    {VISIBILITY_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => { setVisibility(opt.value); setShowVisibilityMenu(false); }}
                        className={`w-full px-4 py-3 text-left flex flex-col gap-0.5 hover:bg-white/[0.04] transition-colors ${visibility === opt.value ? 'bg-[#A38BFF]/10' : ''}`}
                      >
                        <span className={`text-sm font-medium ${visibility === opt.value ? 'text-[#A38BFF]' : 'text-[#F5F7FF]'}`}>
                          {opt.label}
                        </span>
                        <span className="text-xs text-[#A7B0C8]">{opt.description}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {createGroupMutation.isError && (
              <p className="text-xs text-[#FF4D4D]">Failed to create group. Please try again.</p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
